import type { ErrorResponse } from "@shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { authService } from "../services/auth.service";
import { useAuthStore } from "../store/auth.store";
import type { LoginRequest, RegisterRequest } from "../types";

// ─── Query Key ─────────────────────────────────────────────────────
export const authKeys = {
	all: ["auth"] as const,
	me: () => [...authKeys.all, "me"] as const,
};

// ─── useRegister ────────────────────────────────────────────────────
export function useRegister() {
	const { message } = App.useApp();
	const navigate = useNavigate();
	const setAuth = useAuthStore((state) => state.setAuth);

	return useMutation({
		mutationFn: (data: RegisterRequest) => authService.register(data),
		onSuccess: (response) => {
			setAuth(response.user, response.accessToken, response.refreshToken);
			message.success("Muvaffaqiyatli ro'yxatdan o'tdingiz!");
			navigate("/dashboard", { replace: true });
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const errorMessage =
				error.response?.data?.error?.message || "Ro'yxatdan o'tishda xatolik yuz berdi";
			message.error(errorMessage);
		},
	});
}

// ─── useLogin ───────────────────────────────────────────────────────
export function useLogin() {
	const { message } = App.useApp();
	const navigate = useNavigate();
	const location = useLocation();
	const setAuth = useAuthStore((state) => state.setAuth);

	return useMutation({
		mutationFn: (data: LoginRequest) => authService.login(data),
		onSuccess: (response) => {
			setAuth(response.user, response.accessToken, response.refreshToken);
			message.success("Tizimga muvaffaqiyatli kirdingiz!");

			// ProtectedRoute saqlagan yo'lga redirect (agar bo'lsa)
			const from =
				(location.state as { from?: { pathname: string } })?.from?.pathname || "/dashboard";
			navigate(from, { replace: true });
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const errorMessage =
				error.response?.data?.error?.message || "Telefon raqam yoki parol noto'g'ri";
			message.error(errorMessage);
		},
	});
}

// ─── useLogout ──────────────────────────────────────────────────────
export function useLogout() {
	const { message } = App.useApp();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { refreshToken, logout } = useAuthStore();

	return useMutation({
		mutationFn: () => {
			if (!refreshToken) {
				throw new Error("Refresh token topilmadi");
			}
			return authService.logout({ refreshToken });
		},
		onSuccess: () => {
			logout();
			queryClient.clear();
			message.success("Tizimdan muvaffaqiyatli chiqdingiz!");
			navigate("/login", { replace: true });
		},
		onError: () => {
			// Xato bo'lsa ham logout qilamiz
			logout();
			queryClient.clear();
			navigate("/login", { replace: true });
		},
	});
}

// ─── useMe ──────────────────────────────────────────────────────────
export function useMe() {
	const { setUser, logout, accessToken } = useAuthStore();

	return useQuery({
		queryKey: authKeys.me(),
		queryFn: async () => {
			try {
				const user = await authService.getMe();
				setUser(user);
				return user;
			} catch {
				logout();
				throw new Error("Sessiya muddati tugagan");
			}
		},
		enabled: !!accessToken,
		retry: false,
		staleTime: 1000 * 60 * 5, // 5 daqiqa
		refetchOnWindowFocus: false,
	});
}

// ─── useAuthInit ────────────────────────────────────────────────────
/**
 * App boshlanganda auth holatini tiklash uchun ishlatiladi.
 * localStorage dan tokenlarni o'qib, /auth/me so'rovi orqali
 * user ma'lumotlarini oladi.
 */
export function useAuthInit() {
	const { hydrate, accessToken, isLoading, setLoading } = useAuthStore();
	const { refetch } = useMe();

	const init = async () => {
		hydrate();

		const currentAccessToken = useAuthStore.getState().accessToken;
		if (currentAccessToken) {
			await refetch();
		} else {
			setLoading(false);
		}
	};

	return { init, isLoading, accessToken };
}
